import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { checkoutAPI } from '../services/api';
import Navigation from '../components/layout/Navigation';
import { CheckCircle, Loader, Clock, XCircle } from 'lucide-react';

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const isFreeOrder = searchParams.get('free') === 'true';
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Handle free orders (100% discount)
    if (isFreeOrder && orderId) {
      setStatus('success');
      setPaymentInfo({
        order_id: orderId,
        amount_total: 0,
        currency: 'eur',
        payment_status: 'paid',
        is_free: true
      });
      return;
    }
    
    // Handle regular paid orders
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus();
  }, [sessionId, orderId, isFreeOrder]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    try {
      const response = await checkoutAPI.getStatus(sessionId);
      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        setAttempts(attempts + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 flex items-center justify-center">
        <div className="max-w-md w-full bg-brand-surface border border-brand-border rounded-xl p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader size={48} className="text-violet-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-outfit font-semibold text-white mb-2">Processing Payment...</h1>
              <p className="text-zinc-400">Please wait while we confirm your payment</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-outfit font-semibold text-white mb-2">
                {paymentInfo?.is_free ? '¡Pedido Confirmado!' : 'Payment Successful!'}
              </h1>
              <p className="text-zinc-400 mb-6">
                {paymentInfo?.is_free 
                  ? 'Tu pedido ha sido confirmado. ¡Comenzaremos a preparar tus cartas de inmediato!'
                  : 'Thank you for your order. We\'ll start preparing your cards right away.'
                }
              </p>
              {paymentInfo && (
                <div className="bg-zinc-900 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-zinc-400 mb-1">
                    {paymentInfo.is_free ? 'Total' : 'Amount Paid'}
                  </p>
                  <div className="flex items-center space-x-2">
                    <p className="text-xl font-bold text-violet-600">
                      {paymentInfo.amount_total.toFixed(2)} {paymentInfo.currency.toUpperCase()}
                    </p>
                    {paymentInfo.is_free && (
                      <span className="px-2 py-1 bg-green-900/30 text-green-400 text-xs font-semibold rounded border border-green-600/50">
                        100% DESCUENTO
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <Link to="/dashboard" className="block w-full px-6 py-3 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all">
                  View My Orders
                </Link>
                <Link to="/shop" className="block w-full px-6 py-3 bg-transparent text-white border border-zinc-700 rounded-md font-medium hover:bg-zinc-800 transition-all">
                  Continue Shopping
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle size={48} className="text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-outfit font-semibold text-white mb-2">Payment Error</h1>
              <p className="text-zinc-400 mb-6">There was an error processing your payment. Please contact support.</p>
              <Link to="/cart" className="block w-full px-6 py-3 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all">
                Back to Cart
              </Link>
            </>
          )}

          {(status === 'expired' || status === 'timeout') && (
            <>
              <Clock size={48} className="text-yellow-500 mx-auto mb-4" />
              <h1 className="text-2xl font-outfit font-semibold text-white mb-2">Payment Pending</h1>
              <p className="text-zinc-400 mb-6">Your payment is still being processed. Please check your order history.</p>
              <Link to="/dashboard" className="block w-full px-6 py-3 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all">
                View Orders
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderSuccess;